// Whether the phone page is connected to the PC, and what the orb and the line under it say
// while it isn't. Until the relay leg is open a tap on the orb does nothing (main.ts toggleTalk),
// so the orb must not look like it would: it shows inert (dimmed, no press, a slow breath) with
// "Connecting to your PC…", "Reconnecting to your PC…" after a drop, and after a while, or when
// the PC is offline, a message that says so and a "Try again" button. The relay keeps retrying
// by itself meanwhile; once the leg opens the orb comes back as it was.

import type { RelayStatus } from "./relay-transport";

/** How long a (re)connect may take before the page says it can't reach the PC. */
export const CONNECT_TIMEOUT_MS = 15_000;

export const CONNECTING = "Connecting to your PC…";
export const RECONNECTING = "Reconnecting to your PC…";
export const PC_OFFLINE = "Your PC is offline. Is the voice kit running?";
export const CANT_REACH = "Can't reach your PC. Check that it's on and online.";

export type ConnPhase = "open" | "connecting" | "reconnecting" | "offline" | "failed";

export interface ConnView {
  phase: ConnPhase;
  /** The line under the orb ("" when connected: the mic's state shows instead). */
  label: string;
  /** Show "Try again". */
  retry: boolean;
}

export interface ConnInput {
  status: RelayStatus;
  /**
   * What the page last told the user while it couldn't connect ("offline" or "failed"): it stays
   * so through the relay's own redials (each one says "connecting" again) until the leg opens or
   * the user taps "Try again", so the line and the button don't flicker with every backoff, and
   * one explanation holds (never "offline" and "can't reach" by turns).
   */
  stuck?: "offline" | "failed" | null;
  /** The leg was open at least once since the page loaded (a drop reconnects). */
  everOpen: boolean;
  /** How long the page has been trying since it last was connected (or since "Try again"). */
  waitedMs: number;
  timeoutMs?: number;
}

export function connView({ status, everOpen, waitedMs, stuck = null, timeoutMs = CONNECT_TIMEOUT_MS }: ConnInput): ConnView {
  if (status === "open") return { phase: "open", label: "", retry: false };
  // The PC said it is offline (now, or on an earlier try since the last "Try again").
  if (status === "pc-offline" || stuck === "offline") return { phase: "offline", label: PC_OFFLINE, retry: true };
  if (stuck === "failed" || waitedMs >= timeoutMs) return { phase: "failed", label: CANT_REACH, retry: true };
  return everOpen ? { phase: "reconnecting", label: RECONNECTING, retry: false } : { phase: "connecting", label: CONNECTING, retry: false };
}

/** The orb button's accessible name while it is inert. */
export function orbLabel(view: ConnView): string {
  return view.phase === "open" ? "Voice" : `Voice, unavailable: ${view.label}`;
}
